import { useState, useEffect, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  Avatar,
  Chip,
  Alert,
  alpha,
  Divider,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RouteIcon from '@mui/icons-material/Route';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';

interface DeliveryRoute {
  id: string;
  name: string;
  status: string;
  originLocation: {
    id: string;
    name: string;
  };
  deliveryIds: string[];
  totalDistanceKm: number;
  totalDurationMinutes: number;
  routeData: {
    waypoints: {
      deliveryId: string;
      lat: number;
      lng: number;
      address?: string;
    }[];
  };
}

interface Reservation {
  id: string;
  trackingNumber: string;
  status: string;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
  };
  items: {
    product: {
      name: string;
    };
    quantity: number;
  }[];
  deliveryAddress?: {
    street: string;
    city: string;
    postalCode: string;
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  route: DeliveryRoute | null;
}

const fetchAvailableReservations = async (): Promise<Reservation[]> => {
  const response = await axiosInstance.get('/reservations', {
    params: {
      status: 'ready_for_delivery',
      limit: 100,
    },
  });
  const data = Array.isArray(response.data) ? response.data : response.data.data || [];
  // Filter to only show reservations with delivery addresses (exclude in-store pickup)
  return data.filter((r: Reservation) => r.deliveryAddress && r.deliveryAddress.street);
};

const duplicateRoute = async ({
  routeId,
  reservationIds,
  name,
}: {
  routeId: string;
  reservationIds: string[];
  name?: string;
}): Promise<DeliveryRoute> => {
  const response = await axiosInstance.post(`/delivery-routes/${routeId}/duplicate`, {
    reservationIds,
    name,
  });
  return response.data;
};

export const DuplicateRouteDialog = ({ open, onClose, route }: Props) => {
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const [selectedReservations, setSelectedReservations] = useState<(Reservation | null)[]>([]);
  const [newRouteName, setNewRouteName] = useState('');

  const { data: reservations = [], isLoading: loadingReservations } = useQuery({
    queryKey: ['reservations', 'ready_for_delivery'],
    queryFn: fetchAvailableReservations,
    enabled: open,
  });

  const duplicateMutation = useMutation({
    mutationFn: duplicateRoute,
    onSuccess: (newRoute) => {
      queryClient.invalidateQueries({ queryKey: ['delivery-routes'] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['routed-delivery-ids'] });
      onClose();
      navigate(`/app/deliveries/routes/${newRoute.id}`);
      showMessage('Route duplicated successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to duplicate route', 'error');
    },
  });

  // Initialize selections when route changes
  useEffect(() => {
    if (route) {
      setSelectedReservations(new Array(route.deliveryIds.length).fill(null));
      setNewRouteName(`${route.name} (Copy)`);
    }
  }, [route]);

  const slotsCount = route?.deliveryIds.length || 0;
  const filledCount = selectedReservations.filter((r) => r !== null).length;
  const allSlotsFilled = filledCount === slotsCount;

  // Get reservations that haven't been selected yet
  const getAvailableReservations = (slotIndex: number) => {
    const usedIds = new Set(
      selectedReservations
        .filter((r, i) => r !== null && i !== slotIndex)
        .map((r) => r!.id)
    );
    return reservations.filter((r) => !usedIds.has(r.id));
  };

  const handleSlotChange = (slotIndex: number, reservation: Reservation | null) => {
    const newSelections = [...selectedReservations];
    newSelections[slotIndex] = reservation;
    setSelectedReservations(newSelections);
  };

  const handleDuplicate = () => {
    if (!route || !allSlotsFilled) return;

    const reservationIds = selectedReservations
      .filter((r): r is Reservation => r !== null)
      .map((r) => r.id);

    duplicateMutation.mutate({
      routeId: route.id,
      reservationIds,
      name: newRouteName || undefined,
    });
  };

  const colors =
    mode === 'dark'
      ? {
          bg: palette.background,
          surface: palette.surface,
          surfaceElevated: palette.surfaceElevated,
          border: palette.border,
          borderLight: palette.borderLight,
          textPrimary: palette.textPrimary,
          textSecondary: palette.textSecondary,
          textMuted: palette.textMuted,
        }
      : {
          bg: palette.light.background,
          surface: palette.light.surface,
          surfaceElevated: palette.light.surfaceElevated,
          border: palette.light.border,
          borderLight: palette.light.borderLight,
          textPrimary: palette.light.textPrimary,
          textSecondary: palette.light.textSecondary,
          textMuted: palette.light.textMuted,
        };

  const formatReservationLabel = (reservation: Reservation) => {
    const customerName = `${reservation.customer?.firstName || ''} ${reservation.customer?.lastName || ''}`.trim() || 'Unknown Customer';
    const itemCount = reservation.items?.length || 0;
    return `${reservation.trackingNumber} - ${customerName} (${itemCount} item${itemCount !== 1 ? 's' : ''})`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.md,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: `1px solid ${colors.border}`,
          pb: 2,
        }}
      >
        <Avatar
          sx={{
            bgcolor: alpha(palette.primary, 0.1),
            color: palette.primary,
            width: 48,
            height: 48,
          }}
        >
          <ContentCopyIcon />
        </Avatar>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
            }}
          >
            DUPLICATE ROUTE
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.textMuted,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
            }}
          >
            Select reservations for each delivery slot
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {/* Original Route Summary */}
        {route && (
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: theme.radius.sm,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.surfaceElevated,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: typography.fontFamily.mono,
                color: colors.textMuted,
                fontSize: typography.fontSize.xs,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.widest,
                mb: 1,
              }}
            >
              ORIGINAL ROUTE
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RouteIcon sx={{ fontSize: 18, color: palette.primary }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textPrimary,
                  }}
                >
                  {route.name}
                </Typography>
              </Box>
              <Chip
                size="small"
                icon={<LocationOnIcon sx={{ fontSize: 14 }} />}
                label={route.originLocation?.name}
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                }}
              />
              <Chip
                size="small"
                icon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
                label={`${slotsCount} deliveries`}
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                }}
              />
              <Chip
                size="small"
                label={`${route.totalDistanceKm} km`}
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                }}
              />
              <Chip
                size="small"
                label={`${route.totalDurationMinutes} min`}
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                }}
              />
            </Box>
          </Box>
        )}

        {/* New Route Name */}
        <TextField
          fullWidth
          label="New Route Name"
          value={newRouteName}
          onChange={(e) => setNewRouteName(e.target.value)}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              fontFamily: typography.fontFamily.mono,
            },
          }}
        />

        <Divider sx={{ mb: 3 }} />

        {/* Progress Indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: typography.fontFamily.mono,
              color: colors.textMuted,
              fontSize: typography.fontSize.sm,
            }}
          >
            SLOTS FILLED:
          </Typography>
          <Chip
            label={`${filledCount} / ${slotsCount}`}
            color={allSlotsFilled ? 'success' : 'warning'}
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.bold,
            }}
          />
          {allSlotsFilled && (
            <CheckCircleIcon sx={{ color: palette.success, fontSize: 20 }} />
          )}
        </Box>

        {loadingReservations ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : reservations.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No reservations available for delivery. Reservations must have status "Ready for Delivery" and include a delivery address (not in-store pickup).
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {route?.routeData?.waypoints.map((waypoint, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  borderRadius: theme.radius.sm,
                  border: `1px solid ${selectedReservations[index] ? palette.success : colors.border}`,
                  backgroundColor: selectedReservations[index]
                    ? alpha(palette.success, 0.05)
                    : 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: selectedReservations[index]
                        ? palette.success
                        : palette.warning,
                      fontSize: typography.fontSize.sm,
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.bold,
                    }}
                  >
                    {index + 1}
                  </Avatar>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      color: colors.textPrimary,
                      fontSize: typography.fontSize.sm,
                    }}
                  >
                    DELIVERY SLOT {index + 1}
                  </Typography>
                  {waypoint.address && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.display,
                      }}
                    >
                      (Original: {waypoint.address})
                    </Typography>
                  )}
                </Box>

                <Autocomplete
                  value={selectedReservations[index]}
                  onChange={(_e, value) => handleSlotChange(index, value)}
                  options={getAvailableReservations(index)}
                  getOptionLabel={(option) => formatReservationLabel(option)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              fontWeight: typography.fontWeight.bold,
                              color: colors.textPrimary,
                            }}
                          >
                            {option.trackingNumber}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.textMuted,
                            }}
                          >
                            {option.customer?.firstName} {option.customer?.lastName}
                          </Typography>
                        </Box>
                        {option.deliveryAddress && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: 12, color: colors.textMuted }} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.textMuted,
                                fontSize: typography.fontSize.xs,
                              }}
                            >
                              {option.deliveryAddress.street}, {option.deliveryAddress.city}
                            </Typography>
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.textMuted,
                            fontSize: typography.fontSize.xs,
                          }}
                        >
                          {option.items?.length || 0} item(s):{' '}
                          {option.items?.map((i) => i.product?.name).join(', ').slice(0, 60)}
                          {(option.items?.map((i) => i.product?.name).join(', ').length || 0) > 60 ? '...' : ''}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select a reservation..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <PersonIcon sx={{ fontSize: 18, color: colors.textMuted, mr: 1 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            ))}
          </Box>
        )}

        {duplicateMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(duplicateMutation.error as any)?.response?.data?.message ||
              'Failed to duplicate route. Please try again.'}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${colors.border}` }}>
        <Button
          onClick={onClose}
          disabled={duplicateMutation.isPending}
          sx={{
            fontFamily: typography.fontFamily.mono,
            fontWeight: typography.fontWeight.bold,
            textTransform: 'uppercase',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleDuplicate}
          disabled={!allSlotsFilled || duplicateMutation.isPending || reservations.length === 0}
          startIcon={duplicateMutation.isPending ? <CircularProgress size={16} /> : <ContentCopyIcon />}
          sx={{
            fontFamily: typography.fontFamily.mono,
            fontWeight: typography.fontWeight.bold,
            textTransform: 'uppercase',
            bgcolor: palette.primary,
            '&:hover': { bgcolor: alpha(palette.primary, 0.9) },
          }}
        >
          {duplicateMutation.isPending ? 'Creating...' : 'Create Route'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateRouteDialog;
